import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";

export default function TermsOfService() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-950 text-slate-300' : 'bg-slate-50 text-slate-700'}`} data-testid="terms-of-service-page">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="ghost" className={`mb-4 ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <FileText className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Terms of Service</h1>
          </div>
          <p className={`${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Last updated: February 2025</p>
        </div>

        {/* Content */}
        <div className={`space-y-8 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>1. Acceptance of Terms</h2>
            <p className="leading-relaxed">
              By accessing or using My Business Portal ("the Service"), you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access the Service.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>2. Description of Service</h2>
            <p className="leading-relaxed">
              My Business Portal is a business management platform that provides tools for inventory management, invoicing, customer management, and payment processing. The Service is designed for small to medium-sized businesses, particularly in the service and repair industries.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>3. Account Registration</h2>
            <p className="leading-relaxed mb-3">To use the Service, you must:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Create an account with accurate and complete information</li>
              <li>Be at least 18 years old or have legal authority to enter into this agreement</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
            <p className="leading-relaxed mt-3">
              You are responsible for all activities that occur under your account.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>4. Acceptable Use</h2>
            <p className="leading-relaxed mb-3">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service for any unlawful purpose</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Transmit viruses, malware, or other harmful code</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use the Service to send spam or unsolicited communications</li>
              <li>Impersonate any person or entity</li>
              <li>Collect user data without consent</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>5. Payment Terms</h2>
            <p className="leading-relaxed mb-3">For paid features of the Service:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Fees are billed in advance on a subscription basis</li>
              <li>All fees are non-refundable unless otherwise stated</li>
              <li>We reserve the right to change pricing with 30 days notice</li>
              <li>You are responsible for all applicable taxes</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>6. Data Ownership</h2>
            <p className="leading-relaxed">
              You retain all rights to the data you enter into the Service. We do not claim ownership of your business data, customer information, or other content you create. You grant us a license to use this data solely to provide and improve the Service.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>7. Intellectual Property</h2>
            <p className="leading-relaxed">
              The Service and its original content, features, and functionality are owned by My Business Portal and are protected by international copyright, trademark, and other intellectual property laws. You may not copy, modify, or create derivative works of the Service.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>8. Third-Party Services</h2>
            <p className="leading-relaxed">
              The Service may integrate with third-party services (e.g., payment processors like Stripe and Square). Your use of these services is subject to their respective terms and policies. We are not responsible for the practices of third-party services.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>9. Service Availability</h2>
            <p className="leading-relaxed">
              We strive to maintain high availability but do not guarantee uninterrupted access. We may temporarily suspend the Service for maintenance, updates, or other reasons. We will attempt to provide advance notice when possible.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>10. Limitation of Liability</h2>
            <p className="leading-relaxed">
              To the maximum extent permitted by law, My Business Portal shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities, arising from your use of the Service.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>11. Disclaimer of Warranties</h2>
            <p className="leading-relaxed">
              The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the Service will be error-free, secure, or available at all times.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>12. Indemnification</h2>
            <p className="leading-relaxed">
              You agree to indemnify and hold harmless My Business Portal and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Service or violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>13. Termination</h2>
            <p className="leading-relaxed mb-3">We may terminate or suspend your account immediately, without prior notice, for:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Breach of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Non-payment of fees</li>
              <li>Extended inactivity</li>
            </ul>
            <p className="leading-relaxed mt-3">
              Upon termination, your right to use the Service will cease immediately. You may export your data before termination.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>14. Changes to Terms</h2>
            <p className="leading-relaxed">
              We reserve the right to modify these Terms at any time. We will provide notice of significant changes by posting the new Terms on this page and updating the "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>15. Governing Law</h2>
            <p className="leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>16. Contact Information</h2>
            <p className="leading-relaxed">
              If you have any questions about these Terms, please contact us at:{" "}
              <a href="mailto:support@mybusinessportal.info" className="text-blue-500 hover:underline">
                support@mybusinessportal.info
              </a>
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className={`mt-12 pt-8 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/privacy" className="text-blue-500 hover:underline">Privacy Policy</Link>
            <span className={isDark ? 'text-slate-700' : 'text-slate-300'}>|</span>
            <Link to="/" className="text-blue-500 hover:underline">Back to My Business Portal</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
